export const BOOKLIST_QUOTATION_DISCLAIMER = "This quotation is for informational purposes only and all pricing is subject to change without notice. Please contact eKitabu for a firm quotation.";

export const DEFAULT_BOOKLIST_EXPORT_LABELS = {
  headers: [
    "Title",
    "Author",
    "Publisher",
    "ISBN",
    "Format",
    "Category",
    "Link to book detail page",
    "RRP / list price",
    "Discounted price / final price",
    "Total savings",
    "Price VAT inclusive",
    "Price without VAT",
    "VAT component",
    "Currency",
    "Quantity",
    "Line total",
  ],
  generatedDateLabel: "Generated date",
  disclaimerLabel: "Disclaimer",
  unavailableLegacyProduct: "Unavailable / legacy product",
};


export function buildBookListExportCsv(bookList, localBooksMap, options = {}) {
  const {
    generatedAt = new Date(),
    origin = "",
    disclaimer = BOOKLIST_QUOTATION_DISCLAIMER,
  } = options;
  const labels = {
    ...DEFAULT_BOOKLIST_EXPORT_LABELS,
    ...options.labels,
    headers: options.labels?.headers || DEFAULT_BOOKLIST_EXPORT_LABELS.headers,
  };
  const rows = [
    labels.headers,
    ...buildBookListExportRows(bookList, localBooksMap, { origin, labels }),
    [],
    [labels.generatedDateLabel, formatGeneratedDate(generatedAt)],
    [labels.disclaimerLabel, disclaimer],
  ];

  return `\ufeff${rows.map(toCsvRow).join("\r\n")}`;
}

export function buildBookListExportRows(bookList, localBooksMap = {}, options = {}) {
  const normalizedOptions = typeof options === 'string' ? { origin: options } : options;
  const { origin = "", labels = DEFAULT_BOOKLIST_EXPORT_LABELS } = normalizedOptions;
  const taxRate = Number(bookList?.taxRate?.rate || 0);
  const discountPercentage = Number(bookList?.discountPercentage || 0);

  return (bookList?.bookListItems || []).map((item) => {
    const localBook = localBooksMap[`/local_books/${item.productId}`] || null;
    const quantity = item.quantity || 1;
    const pricing = calculateExportPricing(item, localBook, taxRate, discountPercentage);

    // Column layout (indexes 7-15). Per-unit prices: list price, final price,
    // and the final price's VAT decomposition (inclusive / exclusive / component).
    // "Price VAT inclusive" equals "final price" by definition - it is the
    // inclusive leg of the decomposition, kept so the incl/excl/component triple
    // reads as a self-contained VAT breakdown. "Total savings" and "Line total"
    // are line-level (x quantity); everything else is per-unit.
    return [
      localBook?.title || item.name || labels.unavailableLegacyProduct,
      authorNames(localBook),
      localBook?.publisher?.name || "",
      localBook?.isbn || "",
      localBook?.format?.mimeType || "",
      categoryNames(localBook),
      productLink(localBook, origin),
      formatNumber(pricing.listVatInclusive),
      formatNumber(pricing.finalVatInclusive),
      formatNumber(pricing.totalSavings * quantity),
      formatNumber(pricing.finalVatInclusive),
      formatNumber(pricing.finalVatExclusive),
      formatNumber(pricing.finalVatComponent),
      currencyId(localBook?.currency) || currencyId(item.baseCurrency),
      quantity,
      formatNumber(pricing.finalVatInclusive * quantity),
    ];
  });
}


/**
 * Compute quotation pricing for a single booklist item.
 *
 * Unit conventions (must match the rest of the app, e.g. priceCalculator.js):
 * - `taxRate` is a fraction, NOT a percentage: 0.16 means 16% VAT.
 * - `discountPercentage` is a percentage in the range 0-100: 10 means 10% off.
 *
 * @param {Object} item - booklist item (basePrice, vatIncluded fallbacks)
 * @param {Object|null} localBook - live product metadata (price, vatIncluded)
 * @param {number} taxRate - VAT rate as a fraction (0.16 = 16%)
 * @param {number} discountPercentage - discount as a percent (0-100)
 */
export function calculateExportPricing(item, localBook, taxRate, discountPercentage) {
  // Pair each price with its own VAT flag: a live price uses the live book's
  // flag, while a fallback to the snapshot basePrice must use the snapshot's
  // flag - mixing them misprices the VAT decomposition when the two differ.
  const livePrice = numberOrNull(localBook?.price);
  const snapshotPrice = numberOrNull(item?.basePrice);
  const rawPrice = livePrice ?? snapshotPrice ?? 0;
  // Each price source pairs ONLY with its own flag: a null live flag means
  // "not VAT-inclusive", never "borrow the snapshot's flag".
  const vatIncluded = livePrice !== null
    ? (localBook?.vatIncluded ?? false)
    : (item?.vatIncluded ?? false);
  const normalizedTaxRate = Math.max(Number(taxRate || 0), 0);
  const normalizedDiscount = Math.min(Math.max(Number(discountPercentage || 0), 0), 100) / 100;
  const taxMultiplier = 1 + normalizedTaxRate;

  const listVatInclusive = vatIncluded || normalizedTaxRate === 0
    ? rawPrice
    : rawPrice * taxMultiplier;
  const listVatExclusive = vatIncluded && normalizedTaxRate > 0
    ? rawPrice / taxMultiplier
    : rawPrice;

  const finalVatInclusive = listVatInclusive * (1 - normalizedDiscount);
  const finalVatExclusive = normalizedTaxRate > 0
    ? finalVatInclusive / taxMultiplier
    : finalVatInclusive;

  return {
    listVatInclusive,
    listVatExclusive,
    listVatComponent: listVatInclusive - listVatExclusive,
    finalVatInclusive,
    finalVatExclusive,
    finalVatComponent: finalVatInclusive - finalVatExclusive,
    totalSavings: listVatInclusive - finalVatInclusive,
  };
}

function productLink(localBook, origin) {
  if (!localBook?.slug) return "";
  return `${origin}/local-book/${localBook.slug}`;
}

function categoryNames(localBook) {
  const edges = localBook?.subjects?.edges || [];
  return edges.map((edge) => edge?.node?.heading).filter(Boolean).join(" | ");
}

function authorNames(localBook) {
  const bookAuthors = localBook?.bookAuthors;

  // Real LocalBook shape: bookAuthors is an array of { sequence, author: { name } }.
  if (Array.isArray(bookAuthors)) {
    return [...bookAuthors]
      .sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
      .map((entry) => entry?.author?.name)
      .filter(Boolean)
      .join(" | ");
  }

  // Defensive: GraphQL connection shape (bookAuthors.edges[].node.author.name).
  if (bookAuthors?.edges) {
    return bookAuthors.edges.map((edge) => edge?.node?.author?.name).filter(Boolean).join(" | ");
  }

  // Fallback: plain author list (strings or { name }), e.g. legacy Book shape.
  if (Array.isArray(localBook?.authors)) {
    return localBook.authors
      .map((author) => (typeof author === "string" ? author : author?.name))
      .filter(Boolean)
      .join(" | ");
  }

  return "";
}

function currencyId(currency) {
  if (!currency) return "";
  if (currency._id) return currency._id;
  if (currency.id) return currency.id.split("/").pop();
  return "";
}

// Treat null/undefined/"" as absent (Number(null) === 0 would silently turn a
// missing price into a free book) and reject non-finite parses.
function numberOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatNumber(value) {
  return Number(value || 0).toFixed(2);
}

function formatGeneratedDate(value) {
  return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
}

function toCsvRow(row) {
  return row.map(toCsvCell).join(",");
}

function toCsvCell(value) {
  let text = value == null ? "" : String(value);
  // Neutralize CSV/formula injection: spreadsheets treat a leading =, +, -, or
  // @ as a formula even inside quotes, and a leading tab/CR/LF can hide one too.
  if (/^[=+\-@\t\r\n]/.test(text)) {
    text = `'${text}`;
  }
  return `"${text.replace(/"/g, '""')}"`;
}
